import { TransporterPackageStatus, VehicleStatus, PickupLocation, DropoffLocation, RepairShopLocation } from '@otw/models'

// Type guard to check if a location is a RepairShopLocation
function isRepairShopLocation(location: PickupLocation | DropoffLocation | RepairShopLocation): location is RepairShopLocation {
  return 'shop_type' in location && Array.isArray(location.shop_type);
}

/**
 * Formats a package status for display
 */
export const formatPackageStatus = (status: TransporterPackageStatus | string): string => {
  switch (status) {
    case 'DRAFT':
      return 'Draft'
    case 'ASSIGNED':
      return 'Assigned'
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'COMPLETED':
      return 'Completed'
    case 'CANCELLED':
      return 'Cancelled'
    case 'ALL':
      return 'All'
    default:
      return status
  }
}

/**
 * Formats transporter name from transporter object
 */
export const formatTransporterName = (transporter: any): string => {
  if (!transporter) return 'Unassigned'
  
  const firstName = transporter.first_name || ''
  const lastName = transporter.last_name || ''
  const email = transporter.email || ''
  
  const fullName = `${firstName} ${lastName}`.trim()
  
  if (fullName) return fullName
  if (email) return email
  return 'Unknown'
}

/**
 * Gets status color for UI display
 */
export const getStatusColor = (status: TransporterPackageStatus): string => {
  switch (status) {
    case 'DRAFT':
      return 'warning.main'
    case 'ASSIGNED':
      return 'info.main'
    case 'IN_PROGRESS':
      return 'primary.main'
    case 'COMPLETED':
      return 'success.main'
    case 'CANCELLED':
      return 'error.main'
    default:
      return 'text.secondary'
  }
}

/**
 * Gets status background color for UI display
 */
export const getStatusBackgroundColor = (status: TransporterPackageStatus): string => {
  switch (status) {
    case 'DRAFT':
      return 'warning.50'
    case 'ASSIGNED':
      return 'info.50'
    case 'IN_PROGRESS':
      return 'primary.50'
    case 'COMPLETED':
      return 'success.50'
    case 'CANCELLED':
      return 'error.50'
    default:
      return 'grey.50'
  }
}

/**
 * Formats vehicle status for display
 */
export const formatVehicleStatus = (status: string): string => {
  switch (status) {
    case 'Assigned':
      return 'Assigned'
    case 'In Transit':
      return 'In Transit'
    case 'Completed':
      return 'Completed'
    case 'Repair':
      return 'Repair'
    default:
      return 'Not Assigned'
  }
}

/**
 * Gets vehicle status color for UI display
 */
export const getVehicleStatusColor = (status: string): string => {
  switch (status) {
    case 'Assigned':
      return 'info.main'
    case 'In Transit':
      return 'primary.main'
    case 'Completed':
      return 'success.main'
    case 'Repair':
      return 'error.main'
    default:
      return 'text.secondary'
  }
}

/**
 * Gets vehicle status color prop for MUI components (Chip, etc.)
 */
export const getVehicleStatusColorProp = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'Assigned':
      return 'info'
    case 'In Transit':
      return 'primary'
    case 'Completed':
      return 'success'
    case 'Repair':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Gets the latest vehicle status from status array
 */
export const getLatestVehicleStatus = (vehicleStatuses: any[]): string => {
  if (!vehicleStatuses || vehicleStatuses.length === 0) {
    return 'Not Assigned'
  }
  
  const latestStatus = vehicleStatuses[vehicleStatuses.length - 1]
  return latestStatus?.status || 'Not Assigned'
}

/**
 * Generates a text message based on vehicle status and location
 */
export const generateVehicleStatusMessage = (params: {
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    stock_number?: string | null;
  };
  status: 'Assigned' | 'In Transit' | 'Completed' | 'Repair' | 'Not Assigned';
  clientName?: string;
  pickupLocation?: {
    city?: string;
    state?: string;
    address?: string;
  };
  dropoffLocation?: {
    city?: string;
    state?: string;
    address?: string;
  };
  interimLocation?: {
    city?: string;
    state?: string;
    address?: string;
  };
}): string => {
  const { vehicle, status, clientName, pickupLocation, dropoffLocation, interimLocation } = params;
  
  const vehicleInfo = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
  const greeting = clientName ? `Hi ${clientName.split(' ')[0]},` : 'Hi,';
  
  const formatLocation = (location?: { city?: string; state?: string; address?: string }): string => {
    if (!location) return '';
    const parts: string[] = [];
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (parts.length > 0) return parts.join(', ');
    if (location.address) return location.address;
    return '';
  };
  
  switch (status) {
    case 'Assigned':
      const pickupLoc = formatLocation(pickupLocation);
      if (pickupLoc) {
        return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} has been assigned to a transporter for pickup from ${pickupLoc}.`;
      }
      return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} has been assigned to a transporter for transport. We'll contact you with updates soon.`;
    
    case 'In Transit':
      const dropoffLoc = formatLocation(dropoffLocation);
      if (dropoffLoc) {
        return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} is currently in transit and on its way to ${dropoffLoc}.`;
      }
      return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} is currently in transit.`;
    
    case 'Repair':
      const repairLoc = formatLocation(interimLocation);
      if (repairLoc) {
        return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} is currently at the repair shop in ${repairLoc}.`;
      }
      return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} is currently at the repair shop.`;
    
    case 'Completed':
      const completedDropoffLoc = formatLocation(dropoffLocation);
      if (completedDropoffLoc) {
        return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} has been successfully delivered to ${completedDropoffLoc}. Thank you for choosing our transport services!`;
      }
      return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} Your ${vehicleInfo} has been successfully delivered. Thank you for choosing our transport services!`;
    
    default:
      return `This is an automated message from Optic Truck Works. Do not reply. ${greeting} We have an update regarding your ${vehicleInfo}. We'll contact you with more details soon.`;
  }
}

/**
 * Formats a location for display (city, state or address)
 */
const formatLocationDisplay = (location: PickupLocation | DropoffLocation | RepairShopLocation | undefined): string => {
  if (!location) return ''
  
  // For repair shops, prefer name if available
  if (isRepairShopLocation(location) && location.name) {
    return location.name
  }
  
  // Otherwise use city, state or address
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`
  }
  if (location.address) {
    return location.address
  }
  return ''
}

/**
 * Formats a location with full details for display
 * Returns an object with title and details for structured display
 */
const formatLocationDetails = (location: PickupLocation | DropoffLocation | RepairShopLocation | undefined): { title: string; details: string[] } => {
  if (!location) {
    return { title: '', details: [] }
  }
  
  const details: string[] = []
  let title = ''
  
  // For repair shops, use name as title if available
  if (isRepairShopLocation(location) && location.name) {
    title = location.name
  } else if (location.contact_name) {
    title = location.contact_name
  }
  
  // Add address details
  if (location.address) {
    details.push(location.address)
  }
  
  // Add city, state, zip
  const locationParts: string[] = []
  if (location.city) locationParts.push(location.city)
  if (location.state) locationParts.push(location.state)
  if (location.zip) locationParts.push(location.zip)
  if (locationParts.length > 0) {
    details.push(locationParts.join(', '))
  }
  
  // Add contact phone if available
  if (location.contact_phone) {
    details.push(`Phone: ${location.contact_phone}`)
  }
  
  // Add notes if available
  if (location.notes) {
    details.push(`Notes: ${location.notes}`)
  }
  
  // If no title was set, use first detail or fallback
  if (!title && details.length > 0) {
    title = details[0]
    details.shift() // Remove from details since it's now the title
  }
  
  return { title, details }
}

/**
 * Gets the "from" location from previous status entries
 */
const getFromLocation = (
  currentStatus: VehicleStatus,
  allStatuses: VehicleStatus[]
): PickupLocation | DropoffLocation | RepairShopLocation | undefined => {
  if (!allStatuses || allStatuses.length === 0) return undefined
  
  const currentIndex = allStatuses.findIndex(s => s === currentStatus)
  if (currentIndex <= 0) return undefined
  
  // Look backwards through statuses to find the last location
  for (let i = currentIndex - 1; i >= 0; i--) {
    const status = allStatuses[i]
    // If previous status had a current_location, that's where we came from
    if (status.current_location) {
      return status.current_location
    }
    // If previous status was in transit, use its next_location as the "from"
    if (status.next_location) {
      return status.next_location
    }
  }
  
  return undefined
}

/**
 * Formats the current location display for a vehicle status
 * Returns a human-readable string like "At Pickup Location" or "In transit between {from} and {to}"
 */
export const formatCurrentLocationDisplay = (
  currentStatus: VehicleStatus | undefined,
  allStatuses: VehicleStatus[] = []
): string => {
  if (!currentStatus) {
    return 'Location unknown'
  }
  
  // If vehicle is at a location
  if (currentStatus.current_location) {
    const location = currentStatus.current_location
    const locationName = formatLocationDisplay(location)
    
    // Determine location type for better display
    if ('name' in location && location.name) {
      return `At ${location.name}`
    }
    if (location.city && location.state) {
      return `At ${location.city}, ${location.state}`
    }
    if (location.address) {
      return `At ${location.address}`
    }
    return 'At location'
  }
  
  // If vehicle is in transit
  if (currentStatus.next_location) {
    const toLocation = formatLocationDisplay(currentStatus.next_location)
    const fromLocation = formatLocationDisplay(getFromLocation(currentStatus, allStatuses))
    
    if (fromLocation && toLocation) {
      return `In transit between ${fromLocation} and ${toLocation}`
    }
    if (toLocation) {
      return `In transit to ${toLocation}`
    }
    return 'In transit'
  }
  
  return 'Location unknown'
}

/**
 * Formats the current location with full details for structured display
 * Returns an object with title, subtitle, and details array
 */
export const formatCurrentLocationDetails = (
  currentStatus: VehicleStatus | undefined,
  allStatuses: VehicleStatus[] = []
): { title: string; subtitle?: string; details: string[] } => {
  if (!currentStatus) {
    return { title: 'Location unknown', details: [] }
  }
  
  // If vehicle is at a location
  if (currentStatus.current_location) {
    const location = currentStatus.current_location
    const locationDetails = formatLocationDetails(location)
    
    // Only show subtitle for repair shops
    let subtitle: string | undefined = undefined
    if (isRepairShopLocation(location)) {
      subtitle = 'Repair Shop'
    }
    
    return {
      title: locationDetails.title || 'Location',
      subtitle: subtitle,
      details: locationDetails.details
    }
  }
  
  // If vehicle is in transit
  if (currentStatus.next_location) {
    const toLocation = currentStatus.next_location
    const fromLocation = getFromLocation(currentStatus, allStatuses)
    
    // Get location titles (name for repair shops, contact_name for others)
    const getLocationTitle = (location: PickupLocation | DropoffLocation | RepairShopLocation | undefined): string => {
      if (!location) return ''
      if (isRepairShopLocation(location) && location.name) {
        return location.name
      }
      if (location.contact_name) {
        return location.contact_name
      }
      // Fallback to city, state if no name available
      const parts: string[] = []
      if (location.city) parts.push(location.city)
      if (location.state) parts.push(location.state)
      return parts.join(', ') || location.address || 'Location'
    }
    
    // Format city and state for display in details
    const formatCityState = (location: PickupLocation | DropoffLocation | RepairShopLocation | undefined): string => {
      if (!location) return ''
      const parts: string[] = []
      if (location.city) parts.push(location.city)
      if (location.state) parts.push(location.state)
      return parts.join(', ')
    }
    
    const fromTitle = getLocationTitle(fromLocation)
    const toTitle = getLocationTitle(toLocation)
    const fromCityState = formatCityState(fromLocation)
    const toCityState = formatCityState(toLocation)
    
    if (fromTitle && toTitle) {
      return {
        title: `In transit between ${fromTitle} and ${toTitle}`,
        details: [
          ...(fromCityState ? [`From: ${fromCityState}`] : []),
          ...(toCityState ? [`To: ${toCityState}`] : [])
        ]
      }
    }
    if (toTitle) {
      return {
        title: `In transit to ${toTitle}`,
        details: toCityState ? [`To: ${toCityState}`] : []
      }
    }
    return {
      title: 'In transit',
      details: []
    }
  }
  
  return { title: 'Location unknown', details: [] }
}
